import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Plus, Link2, CheckCircle, ExternalLink, Unlink, ClipboardList,
  ShieldCheck, Loader2, Lock, ShieldAlert
} from 'lucide-react';
import api from '../../utils/axios';
import { toast } from 'sonner';

const CasoSolicitudes = ({
  caso, solicitudesVinculadas, onOpenVincular, onOpenNuevaSolicitud,
  onDesvincular, onRefresh, navigate
}) => {
  const [validandoBloqueo, setValidandoBloqueo] = useState(null);

  const handleCompletar = async (sol) => {
    const cerrar = window.confirm('Desea cerrar el caso al completar esta solicitud?');
    try {
      await api.post(`/approvals/${sol.id}/completar-y-cerrar-caso?cerrar_caso=${cerrar}`);
      toast.success(cerrar ? 'Solicitud completada y caso cerrado' : 'Solicitud completada');
      onRefresh();
    } catch (error) {
      toast.error('Error al completar la solicitud');
    }
  };

  const handleValidarBloqueo = async (sol) => {
    const cvu = sol.cvu_bloqueo || sol.campos_adicionales?.cvu_bloqueo || caso.cuentas_relacionadas?.[0] || caso.cuit;
    if (!cvu) {
      toast.error('No se encontró CVU/CUIT para validar el bloqueo');
      return;
    }

    setValidandoBloqueo(sol.id);
    try {
      const tipoBusqueda = cvu.replace(/-/g, '').replace(/ /g, '').length >= 20 ? 'cvu' : 'cuit';
      const res = await api.post('/waya/validar-bloqueo', {
        caso_id: caso.id,
        solicitud_id: sol.id,
        cvu: cvu,
        tipo: tipoBusqueda,
      });

      const data = res.data;
      if (data.success) {
        if (data.accion === 'EN_PROGRESO') {
          toast.success('Validación de bloqueo iniciada. Recibirás una notificación con el resultado.', { duration: 5000 });
        } else if (data.accion === 'YA_BLOQUEADA') {
          toast.success('La cuenta ya estaba bloqueada en Waya. Caso actualizado.');
        } else if (data.accion === 'BLOQUEADA') {
          toast.success('Cuenta bloqueada exitosamente en Waya. Caso actualizado.');
        }
        onRefresh();
      } else {
        toast.error(data.error || 'No se pudo validar/bloquear la cuenta');
      }
    } catch (error) {
      toast.error('Error al validar bloqueo: ' + (error.response?.data?.detail || error.message));
    } finally {
      setValidandoBloqueo(null);
    }
  };

  const getEsBloqueo = (sol) => (sol.tipo_solicitud || sol.subtipo || sol.accion || '').toUpperCase().includes('BLOQUEO');

  // Check if there's a BLOQUEO solicitud
  const tieneBloqueo = solicitudesVinculadas.some(s => getEsBloqueo(s));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Solicitudes Vinculadas</h3>
        <div className="flex gap-2">
          <Button
            onClick={onOpenVincular}
            variant="outline"
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
            data-testid="vincular-solicitud-btn"
          >
            <Link2 size={16} className="mr-2" /> Vincular Existente
          </Button>
          <Button
            onClick={onOpenNuevaSolicitud}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus size={16} className="mr-2" /> Nueva Solicitud
          </Button>
        </div>
      </div>

      {solicitudesVinculadas.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
          <p>No hay solicitudes vinculadas a este caso</p>
          <p className="text-sm mt-2">Cree una solicitud para iniciar un tramite (bloqueo, desvinculacion, etc.)</p>
        </div>
      ) : (
        <div className="space-y-3">
          {solicitudesVinculadas.map((sol) => {
            const esBloqueo = getEsBloqueo(sol);
            return (
              <Card key={sol.id} className={`p-4 border-l-4 ${
                sol.estado === 'PENDIENTE' ? 'border-l-amber-500' :
                sol.estado === 'COMPLETADA' ? 'border-l-green-500' :
                sol.estado === 'RECHAZADA' ? 'border-l-red-500' :
                'border-l-slate-300'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-slate-800">{sol.entidad_nombre}</h4>
                      <Badge variant="outline" className={`text-xs ${
                        sol.estado === 'PENDIENTE' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        sol.estado === 'COMPLETADA' ? 'bg-green-50 text-green-700 border-green-200' :
                        sol.estado === 'RECHAZADA' ? 'bg-red-50 text-red-700 border-red-200' :
                        'bg-slate-50 text-slate-700'
                      }`}>
                        {sol.estado}
                      </Badge>
                      {esBloqueo && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                          <Lock size={10} className="mr-1" /> Bloqueo
                        </Badge>
                      )}
                      {sol.cerrar_caso_al_completar && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                          Cierra caso
                        </Badge>
                      )}
                      {sol.waya_estado === 'BLOQUEADO' && (
                        <Badge className="text-xs bg-red-100 text-red-700">
                          <ShieldCheck size={10} className="mr-1" /> Bloqueado en Waya
                        </Badge>
                      )}
                      {sol.waya_estado === 'VALIDANDO' && (
                        <Badge className="text-xs bg-blue-100 text-blue-700 animate-pulse">
                          <Loader2 size={10} className="mr-1 animate-spin" /> Validando...
                        </Badge>
                      )}
                      {sol.waya_estado === 'ERROR_BLOQUEO' && (
                        <Badge className="text-xs bg-red-100 text-red-600">
                          Error Bloqueo
                        </Badge>
                      )}
                      {sol.waya_estado === 'PENDIENTE_BLOQUEO' && (
                        <Badge className="text-xs bg-amber-100 text-amber-700 animate-pulse">
                          Bloqueo Pendiente
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{sol.descripcion}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Creada: {(() => { const d = new Date(sol.fecha_creacion || sol.created_at); return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-AR'); })()} por {sol.solicitante_email || sol.creado_por || '-'}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {esBloqueo && !caso.cuenta_bloqueada && sol.waya_estado !== 'BLOQUEADO' && sol.waya_estado !== 'VALIDANDO' && (
                      <Button
                        size="sm"
                        onClick={() => handleValidarBloqueo(sol)}
                        disabled={validandoBloqueo === sol.id}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid={`validar-bloqueo-${sol.id}`}
                      >
                        {validandoBloqueo === sol.id ? (
                          <><Loader2 size={14} className="mr-1 animate-spin" /> Validando...</>
                        ) : (
                          <><ShieldAlert size={14} className="mr-1" /> Validar Bloqueo</>
                        )}
                      </Button>
                    )}
                    {esBloqueo && (caso.cuenta_bloqueada || sol.waya_estado === 'BLOQUEADO') && (
                      <Badge className="text-xs bg-green-100 text-green-700 py-1.5 px-3">
                        <ShieldCheck size={14} className="mr-1" /> Bloqueada
                      </Badge>
                    )}
                    {sol.estado === 'PENDIENTE' && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleCompletar(sol)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <CheckCircle size={14} className="mr-1" /> Completar
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      onClick={() => navigate(`/m/tramites/solicitudes/${sol.id}`)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      <ExternalLink size={14} className="mr-1" /> Ver Detalle
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => onDesvincular(sol.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`desvincular-${sol.id}`}
                    >
                      <Unlink size={14} className="mr-1" /> Desvincular
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CasoSolicitudes;
